import React, { useState } from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import { isFieldForListingType } from '../../util/fieldHelpers';
import { getListingSizeInfo } from '../../util/userHelpers';

import { Heading } from '../../components';
import ShoeSizeChartModal from './ShoeSizeChartModal';
import ClothingSizeChartModal from './ClothingSizeChartModal';

import css from './ListingPage.module.css';

const SectionDetailsMaybe = props => {
  const { publicData, metadata = {}, listingFieldConfigs, isFieldForCategory, intl } = props;
  const [isSizeChartOpen, setIsSizeChartOpen] = useState(false);
  const [isClothingSizeChartOpen, setIsClothingSizeChartOpen] = useState(false);

  if (!publicData || !listingFieldConfigs) {
    return null;
  }

  const { sizeLabel, isFootwear, isClothing, hasSize } = getListingSizeInfo(publicData);

  const pickListingFields = (filteredConfigs, config) => {
    const { key, schemaType, enumOptions, showConfig = {} } = config;
    const listingType = publicData.listingType;
    const isTargetListingType = isFieldForListingType(listingType, config);
    const isTargetCategory = isFieldForCategory(config);

    const { isDetail, label } = showConfig;
    const publicDataValue = publicData[key];
    const metadataValue = metadata[key];
    const value = typeof publicDataValue != null ? publicDataValue : metadataValue;

    if (isDetail && isTargetListingType && isTargetCategory && typeof value !== 'undefined') {
      const findSelectedOption = enumValue => enumOptions?.find(o => enumValue === `${o.option}`);
      const getBooleanMessage = value =>
        value
          ? intl.formatMessage({ id: 'SearchPage.detailYes' })
          : intl.formatMessage({ id: 'SearchPage.detailNo' });
      const optionConfig = findSelectedOption(value);

      return schemaType === 'enum'
        ? filteredConfigs.concat({ key, value: optionConfig?.label || value, label })
        : schemaType === 'boolean'
          ? filteredConfigs.concat({ key, value: getBooleanMessage(value), label })
          : schemaType === 'long'
            ? filteredConfigs.concat({ key, value, label })
            : filteredConfigs;
    }
    return filteredConfigs;
  };

  const existingListingFields = listingFieldConfigs.reduce(pickListingFields, []);

  // Add shoe size field for footwear category if it exists
  const shouldShowShoeSize = isFootwear && hasSize;

  if (shouldShowShoeSize) {
    const sizeField = existingListingFields.find(
      f => f.key === 'size' || f.key === 'footwearSize' || f.key === 'shoeSize'
    );

    if (!sizeField) {
      existingListingFields.push({
        key: 'shoeSize',
        value: sizeLabel,
        label: 'Size',
        showSizeChart: true,
      });
    } else {
      sizeField.value = sizeLabel;
      sizeField.showSizeChart = true;
    }
  }

  // Add clothing size field for clothing category if it exists
  const shouldShowClothingSize = isClothing && hasSize;

  if (shouldShowClothingSize) {
    const clothingSizeField = existingListingFields.find(f => f.key === 'clothingSize');

    if (!clothingSizeField) {
      existingListingFields.push({
        key: 'clothingSize',
        value: sizeLabel,
        label: 'Size',
        showClothingSizeChart: true,
      });
    } else {
      clothingSizeField.value = sizeLabel;
      clothingSizeField.showClothingSizeChart = true;
    }
  }

  const hasFields = existingListingFields.length > 0;

  return hasFields ? (
    <section className={css.sectionDetails}>
      <Heading as="h2" rootClassName={css.sectionHeading}>
        <FormattedMessage id="ListingPage.detailsTitle" />
      </Heading>
      <ul className={css.details}>
        {existingListingFields.map(detail => (
          <li key={detail.key} className={css.detailsRow}>
            <span className={css.detailLabel}>{detail.label}</span>
            {detail.showSizeChart ? (
              <span className={css.detailValueContainer}>
                <span className={css.detailValueText}>{detail.value}</span>
                <button
                  className={css.sizeChartButton}
                  onClick={() => setIsSizeChartOpen(true)}
                  type="button"
                >
                  Size Chart
                </button>
              </span>
            ) : detail.showClothingSizeChart ? (
              <span className={css.detailValueContainer}>
                <span className={css.detailValueText}>{detail.value}</span>
                <button
                  className={css.sizeChartButton}
                  onClick={() => setIsClothingSizeChartOpen(true)}
                  type="button"
                >
                  Size Chart
                </button>
              </span>
            ) : (
              <span className={css.detailValueText}>{detail.value}</span>
            )}
          </li>
        ))}
      </ul>

      {shouldShowShoeSize && (
        <ShoeSizeChartModal
          isOpen={isSizeChartOpen}
          onClose={() => setIsSizeChartOpen(false)}
        />
      )}

      {shouldShowClothingSize && (
        <ClothingSizeChartModal
          isOpen={isClothingSizeChartOpen}
          onClose={() => setIsClothingSizeChartOpen(false)}
        />
      )}
    </section>
  ) : null;
};

export default SectionDetailsMaybe;
